/**
 * Resolves the current user's identity (userId/email) and every account
 * transitively linked to them via parentChildLinks (parent or child side, any
 * number of hops — e.g. parent A -> child C -> parent B). Shared by
 * useHasActiveSubscription and the whitelist check in useIsPremium so both
 * "does my family have a paid subscription" and "is anyone in my family
 * whitelisted" walk the exact same set of linked accounts.
 *
 * Reads loggedInUser from localStorage (userId, uid, email); if empty, falls
 * back to Firebase Auth current user so identity is found even when
 * localStorage wasn't set (e.g. WebView, new tab).
 */
import { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { firebaseApp } from './firebaseConfig';
import { getAllConnectedEmails } from './parentStudentFirestore';

const auth = getAuth(firebaseApp);

const LOG = '[LinkedIdentity]';

/** Same as PracticePage/PaymentsPage: prefer userId, then uid, then email. Email normalized to lowercase for Firestore match. */
function getLoggedInUserIdAndEmail(stored: string | null): {
  userId: string | null;
  email: string | null;
} {
  if (!stored) return { userId: null, email: null };
  try {
    const parsed = JSON.parse(stored) as { userId?: string; uid?: string; email?: string };
    const userId = parsed?.userId ?? parsed?.uid ?? parsed?.email ?? null;
    const emailRaw = parsed?.email ?? null;
    const email =
      emailRaw && String(emailRaw).trim() !== '' ? String(emailRaw).trim().toLowerCase() : null;
    return {
      userId: userId && String(userId).trim() !== '' ? String(userId).trim() : null,
      email,
    };
  } catch {
    return { userId: null, email: null };
  }
}

export interface LinkedIdentity {
  userId: string | null;
  email: string | null;
  /** null = not resolved yet; [] = resolved, no linked accounts */
  linkedEmails: string[] | null;
}

export function useLinkedIdentity(): LinkedIdentity {
  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [linkedEmails, setLinkedEmails] = useState<string[] | null>(null);

  useEffect(() => {
    const applyIdentity = (u: string | null, e: string | null, source: string) => {
      console.log(LOG, 'identity', {
        source,
        hasUserId: !!u,
        hasEmail: !!e,
        emailPrefix: e ? `${e.slice(0, 3)}***` : null,
      });
      setUserId(u);
      setEmail(e);
    };
    const stored = localStorage.getItem('loggedInUser');
    const fromStorage = getLoggedInUserIdAndEmail(stored);
    if (fromStorage.userId || fromStorage.email) {
      applyIdentity(fromStorage.userId, fromStorage.email, 'localStorage');
      return;
    }
    console.log(LOG, 'no localStorage, waiting for Firebase Auth');
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        applyIdentity(
          user.uid,
          user.email?.trim() ? user.email.trim().toLowerCase() : null,
          'Firebase Auth',
        );
      } else {
        applyIdentity(null, null, 'Firebase Auth (signed out)');
      }
    });
    return () => unsubscribe();
  }, []);

  // Resolve every account transitively linked to this user (parent or child
  // side, any number of hops). Re-runs whenever the identity's email changes.
  useEffect(() => {
    if (!email) {
      setLinkedEmails([]);
      return;
    }
    let cancelled = false;
    getAllConnectedEmails(email)
      .then((connections) => {
        if (cancelled) return;
        setLinkedEmails(connections.map((c) => c.email.trim().toLowerCase()).filter(Boolean));
      })
      .catch((err) => {
        console.warn(LOG, 'failed to load linked accounts', err);
        if (!cancelled) setLinkedEmails([]);
      });
    return () => {
      cancelled = true;
    };
  }, [email]);

  return { userId, email, linkedEmails };
}
